/**
 * Stage accents — code-driven animated "set dressing" layered over the static
 * parallax backdrops to give them life (the SoR4 touch) with no new art:
 *
 *  - lightningFlash : occasional full-screen storm flash (Dover / Westminster)
 *  - neonPulse      : a softly pulsing additive glow band over a neon skyline
 *  - lightSweep     : a bright streak sweeping across (passing headlights / cranes)
 *
 * Pure config + helpers (no Phaser import → unit-testable). StageScene owns the
 * actual Phaser objects/tweens/timers (see createStageAccents).
 */

export interface LightningFlashAccent {
  readonly kind: 'lightningFlash';
  readonly tint: number;
  /** Random gap between flashes (ms). */
  readonly minGapMs: number;
  readonly maxGapMs: number;
  /** Duration of a single flash blink (ms). */
  readonly flashMs: number;
  readonly peakAlpha: number;
}

export interface NeonPulseAccent {
  readonly kind: 'neonPulse';
  readonly tint: number;
  readonly depth: number;
  /** Vertical band (fractions of screen height) the glow covers. */
  readonly topFrac: number;
  readonly bottomFrac: number;
  readonly baseAlpha: number;
  readonly peakAlpha: number;
  readonly periodMs: number;
}

export interface LightSweepAccent {
  readonly kind: 'lightSweep';
  readonly tint: number;
  readonly depth: number;
  readonly topFrac: number;
  readonly bottomFrac: number;
  /** Streak width as a fraction of screen width. */
  readonly widthFrac: number;
  /** Time to cross the screen (ms) and the pause between sweeps (ms). */
  readonly sweepMs: number;
  readonly gapMs: number;
  readonly peakAlpha: number;
}

export interface WaterShimmerAccent {
  readonly kind: 'waterShimmer';
  readonly tint: number;
  readonly depth: number;
  /** Waterline band (fractions of screen height) the glints scatter across. */
  readonly topFrac: number;
  readonly bottomFrac: number;
  /** Number of glint streaks spread across the band. */
  readonly glints: number;
  readonly periodMs: number;
  readonly peakAlpha: number;
}

export interface PassingTrafficAccent {
  readonly kind: 'passingTraffic';
  readonly depth: number;
  /** Far-road band (fractions of screen height) the lights cross. */
  readonly topFrac: number;
  readonly bottomFrac: number;
  /** Headlight tint (travels →) and taillight tint (travels ←). */
  readonly headTint: number;
  readonly tailTint: number;
  /** Number of vehicle lights spread across the band. */
  readonly count: number;
  /** Time for one light to cross (ms) and the pause before its next pass (ms). */
  readonly crossMs: number;
  readonly gapMs: number;
  readonly peakAlpha: number;
}

export interface DriftHazeAccent {
  readonly kind: 'driftHaze';
  readonly tint: number;
  readonly depth: number;
  /** Band (fractions of screen height) the haze puffs occupy. */
  readonly topFrac: number;
  readonly bottomFrac: number;
  /** Number of drifting puffs spread across the band. */
  readonly puffs: number;
  /** Horizontal drift time across the band (ms) and the alpha-pulse period (ms). */
  readonly driftMs: number;
  readonly periodMs: number;
  readonly peakAlpha: number;
}

export type StageAccent =
  | LightningFlashAccent
  | NeonPulseAccent
  | LightSweepAccent
  | WaterShimmerAccent
  | PassingTrafficAccent
  | DriftHazeAccent;

const lightning = (o: Partial<LightningFlashAccent> = {}): LightningFlashAccent => ({
  kind: 'lightningFlash',
  tint: 0xcdd6ff,
  minGapMs: 3500,
  maxGapMs: 8000,
  flashMs: 140,
  peakAlpha: 0.55,
  ...o,
});

const neon = (o: Partial<NeonPulseAccent> = {}): NeonPulseAccent => ({
  kind: 'neonPulse',
  tint: 0xff4fa3,
  depth: -40,
  topFrac: 0.18,
  bottomFrac: 0.62,
  baseAlpha: 0.05,
  peakAlpha: 0.16,
  periodMs: 2600,
  ...o,
});

const sweep = (o: Partial<LightSweepAccent> = {}): LightSweepAccent => ({
  kind: 'lightSweep',
  tint: 0xfff2cc,
  depth: -38,
  topFrac: 0.2,
  bottomFrac: 0.7,
  widthFrac: 0.08,
  sweepMs: 2600,
  gapMs: 5200,
  peakAlpha: 0.14,
  ...o,
});

const water = (o: Partial<WaterShimmerAccent> = {}): WaterShimmerAccent => ({
  kind: 'waterShimmer',
  tint: 0xbfe6ff,
  depth: -36,
  topFrac: 0.52,
  bottomFrac: 0.66,
  glints: 14,
  periodMs: 1800,
  peakAlpha: 0.5,
  ...o,
});

const traffic = (o: Partial<PassingTrafficAccent> = {}): PassingTrafficAccent => ({
  kind: 'passingTraffic',
  depth: -39,
  topFrac: 0.6,
  bottomFrac: 0.66,
  headTint: 0xfff2cc,
  tailTint: 0xff5a4f,
  count: 4,
  crossMs: 5200,
  gapMs: 6000,
  peakAlpha: 0.5,
  ...o,
});

const haze = (o: Partial<DriftHazeAccent> = {}): DriftHazeAccent => ({
  kind: 'driftHaze',
  tint: 0xb8c4d0,
  depth: -41,
  topFrac: 0.45,
  bottomFrac: 0.72,
  puffs: 5,
  driftMs: 14000,
  periodMs: 5200,
  peakAlpha: 0.12,
  ...o,
});

/** Per-stage accents, keyed by stage number (1–9). Default = none. */
export const STAGE_ACCENTS: Readonly<Record<number, readonly StageAccent[]>> = {
  1: [
    neon({ tint: 0xffa64f, periodMs: 3200 }),
    traffic(),
    haze({ tint: 0xc9b48f, peakAlpha: 0.08 }),
  ], // Camden dusk — sodium glow + traffic + faint dusk haze
  2: [neon({ tint: 0xff4fa3 }), sweep({ tint: 0x66e0ff }), traffic({ count: 5, crossMs: 4600 })], // Soho — pink neon + cyan signs + busy traffic
  3: [
    sweep({ tint: 0xbfe0ff, gapMs: 6500, sweepMs: 3400, peakAlpha: 0.12 }),
    water(),
    haze({ tint: 0xa9bccb, topFrac: 0.4, peakAlpha: 0.14 }),
  ], // Docks — crane + Thames glint + river fog
  // 4 Northern Line (interior) — none
  5: [
    neon({ tint: 0x6cff7a, periodMs: 1800, peakAlpha: 0.2 }),
    haze({ tint: 0x9fe0a8, peakAlpha: 0.16, periodMs: 4200 }),
  ], // Spice Lab — sickly green pulse + chemical haze
  6: [
    neon({ tint: 0x8fa0c0, periodMs: 4200, peakAlpha: 0.1 }),
    traffic({ count: 3, crossMs: 6000, peakAlpha: 0.4 }),
  ], // Bethnal — cold estate glow + sparse traffic
  7: [
    lightning(),
    water({ tint: 0xcfe0ec, glints: 18, peakAlpha: 0.45 }),
    haze({ tint: 0xcfe0ec, puffs: 7, peakAlpha: 0.18 }),
  ], // Dover — storm + sea + sea mist
  8: [neon({ tint: 0x69b7ff }), sweep({ tint: 0xffffff, gapMs: 4200 })], // Canary — corporate blue + window sweep
  9: [
    lightning({ peakAlpha: 0.4, minGapMs: 6000, maxGapMs: 12000 }),
    neon({ tint: 0xffd27f, peakAlpha: 0.12 }),
    water({ tint: 0xd9c98f, topFrac: 0.56, bottomFrac: 0.68, peakAlpha: 0.35 }),
    haze({ tint: 0xd9cfae, puffs: 7, peakAlpha: 0.2, topFrac: 0.38 }),
  ], // Westminster — distant storm + lamplight on the Thames + Manannán's fog
};

export function accentsFor(stage: number): readonly StageAccent[] {
  return STAGE_ACCENTS[stage] ?? [];
}

/** Map a 0..1 value to a flash gap in [minGapMs, maxGapMs]. Pure/deterministic. */
export function flashGap(accent: LightningFlashAccent, rand01: number): number {
  const t = Math.max(0, Math.min(1, rand01));
  return Math.round(accent.minGapMs + t * (accent.maxGapMs - accent.minGapMs));
}

/** Resolve a top/bottom-frac band to pixel center-y + height for a screen height. */
export function bandRect(
  topFrac: number,
  bottomFrac: number,
  height: number,
): { y: number; h: number } {
  const top = topFrac * height;
  const bot = bottomFrac * height;
  return { y: (top + bot) / 2, h: Math.max(1, bot - top) };
}
